/**
 * The custom property carrying the app's interface scale (see the host app's
 * `useUiScale`). Published on `<html>` alongside the CSS `zoom` that actually
 * does the scaling; `1` when nothing has set it.
 */
export const ZOOM_VAR = '--vd-zoom';

/**
 * A viewport length corrected for the interface scale.
 *
 * CSS `zoom` on `<html>` scales the used value of every length inside the
 * element — but viewport units are resolved against the *unzoomed* viewport
 * first, and only then scaled. So at 125% a bare `50vh` is laid out as 62.5% of
 * the window, `100vw - 24px` comes out wider than the screen, and a `86dvh`
 * sheet is taller than the window it's supposed to sit inside. Percentages are
 * fine (they resolve against an already-scaled parent); viewport units are not.
 *
 * Dividing by the scale undoes that. Every viewport unit in a scaled tree has
 * to go through here — a bare `vh`/`vw`/`dvh` is a bug wherever the interface
 * scale is not 1, and the app's *default* desktop scale is 1.25, so "wherever"
 * means "by default".
 *
 * @example
 * maxHeight: vp('50vh')   // → calc(50vh / var(--vd-zoom, 1))
 * maxWidth: vp('100vw - 24px')
 */
export function vp(length: string): string {
  return `calc((${length}) / var(${ZOOM_VAR}, 1))`;
}
